import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import * as recruitmentApi from "../../../../core/data/redux/services/recruitmentApi";
import ProfileTab from "../components/dashboard/ProfileTab";
import ApplicationsTab from "../components/dashboard/ApplicationsTab";
import ApplicationTrackingTimeline from "../components/dashboard/ApplicationTrackingTimeline";
import DocumentsTab from "../components/dashboard/DocumentsTab";
import CareerFooter from "../components/common/CareerFooter";
import { notify } from "../../../../services/toast.config";

const NAV_TABS = [
  { key: "profile", label: "Profile", icon: "ti-user-circle" },
  { key: "applications", label: "My Applications", icon: "ti-briefcase" },
  { key: "documents", label: "Requested Documents", icon: "ti-file-text" },
];

const CandidateDashboard: React.FC = () => {
  const { career_page_uuid } = useParams<{ career_page_uuid: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as "profile" | "applications" | "documents") || "profile";

  const setActiveTab = (tab: "profile" | "applications" | "documents") => {
    setSearchParams({ tab });
    setSelectedApplication(null);
  };

  const [guestToken] = useState<string | null>(localStorage.getItem("guest_token"));
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const primaryColor = settings?.primary_color || "#4f46e5";

  useEffect(() => {
    if (!guestToken) {
      notify.error("Please login to view your dashboard");
      navigate(`/careers/${career_page_uuid || ""}`);
      return;
    }
    if (career_page_uuid) {
      recruitmentApi
        .getPublicCareerPageByUuid(career_page_uuid)
        .then((data: any) => {
          setSettings(data?.settings || null);
          if (data?.settings?.company_id) {
            localStorage.setItem("company_id", data.settings.company_id);
            localStorage.setItem("company_type", data.settings.company_type);
          }
        })
        .catch(() => { })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [guestToken, navigate, career_page_uuid]);

  useEffect(() => {
    const applicationId = searchParams.get("applicationId");

    if (activeTab === "applications" && applicationId) {
      if (selectedApplication?.id == applicationId) return;

      setSelectedApplication((prev: any) =>
        prev?.id == applicationId ? prev : { id: applicationId }
      );
    } else {
      setSelectedApplication(null);
    }
  }, [searchParams, activeTab]);

  const handleLogout = () => {
    localStorage.removeItem("guest_token");
    navigate(`/careers/${career_page_uuid || ""}`);
    notify.success("Logged out successfully");
  };

  if (!guestToken) return null;
  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spin size="large" />
      </div>
    );

  const companyName = settings?.company_name || "Candidate Portal";
  const logoUrl = settings?.company_logo_url;

  const applicationId = searchParams.get("applicationId");
  const isTimelineView = activeTab === "applications" && applicationId;

  return (
    <div style={{ minHeight: "100vh", background: "#f4f5f7", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>


      <header
        style={{
          background: "#ffffff",
          borderBottom: "1px solid #e8eaed",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            margin: "0 auto",
            padding: "0 40px",
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >

          <Link
            to={`/careers/${career_page_uuid || ""}`}
            style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} style={{ height: 32, borderRadius: 6 }} />
            ) : (
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: primaryColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {companyName.charAt(0)}
              </div>
            )}
            <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{companyName}</span>
          </Link>


          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Link
              to={`/careers/${career_page_uuid || ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                color: "#6b7280",
                textDecoration: "none",
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                fontWeight: 500,
                transition: "all 0.15s",
              }}
            >
              <i className="ti ti-search" style={{ fontSize: 14 }} />
              Browse Jobs
            </Link>
            <button
              onClick={handleLogout}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                color: "#ef4444",
                background: "#fff5f5",
                border: "1px solid #fecaca",
                borderRadius: 6,
                padding: "6px 14px",
                cursor: "pointer",
                fontWeight: 500,
                transition: "all 0.15s",
              }}
            >
              <i className="ti ti-logout" style={{ fontSize: 14 }} />
              Logout
            </button>
          </div>
        </div>
      </header>


      <div style={{ margin: "0 auto", padding: "30px 40px" }}>


        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
            {activeTab === "applications" && !selectedApplication ? "My Applications" : ""}
            {activeTab === "applications" && selectedApplication ? "Application Timeline" : ""}
            {activeTab === "profile" ? "My Profile" : ""}
          </h1>
          <p style={{ fontSize: 14, color: "#6b7280", margin: "4px 0 0" }}>
            {activeTab === "applications" && !selectedApplication
              ? "Track all your job applications in one place"
              : ""}
            {activeTab === "applications" && selectedApplication
              ? "Real-time status updates for your application"
              : ""}
            {activeTab === "profile" ? "Manage your candidate profile and resume" : ""}
            {activeTab === "documents" ? "View and upload requested documents" : ""}
          </p>
        </div>


        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            marginBottom: 24,
            border: "1px solid #e8eaed",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "0 20px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >

            {isTimelineView && (
              <button
                onClick={() => {
                  setSearchParams({ tab: "applications" });
                  setSelectedApplication(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  color: "#6b7280",
                  background: "transparent",
                  border: "none",
                  padding: "14px 8px 14px 0",
                  cursor: "pointer",
                  fontWeight: 500,
                  marginRight: 8,
                  borderBottom: "2px solid transparent",
                  marginBottom: -1,
                }}
              >
                <i className="ti ti-arrow-left" style={{ fontSize: 14 }} />
                Back
              </button>
            )}


            {!isTimelineView &&
              NAV_TABS.map(({ key, label, icon }) => {
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setSearchParams({ tab: key });
                      setSelectedApplication(null);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "15px 14px",
                      background: "transparent",
                      border: "none",
                      borderBottom: isActive
                        ? `2px solid ${primaryColor}`
                        : "2px solid transparent",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      fontWeight: isActive ? 600 : 400,
                      fontSize: 14,
                      color: isActive ? primaryColor : "#6b7280",
                      transition: "all 0.2s",
                      marginBottom: -1,
                    }}
                  >
                    <i
                      className={`ti ${icon}`}
                      style={{
                        fontSize: 16,
                        color: isActive ? primaryColor : "#9ca3af",
                      }}
                    />
                    {label}
                  </button>
                );
              })}


            {isTimelineView && (
              <span style={{ fontSize: 13, color: "#9ca3af", padding: "15px 4px" }}>
                Applications &rsaquo;{" "}
                <span style={{ color: "#374151", fontWeight: 500 }}>
                  {selectedApplication?.job_title || "Application"}
                </span>
              </span>
            )}
          </div>


          <div style={{ padding: 24 }}>
            {activeTab === "profile" && <ProfileTab primaryColor={primaryColor} />}
            {activeTab === "applications" && !selectedApplication && (
              <ApplicationsTab
                primaryColor={primaryColor}
                onViewTimeline={(app) => {
                  setSelectedApplication(app);

                  setSearchParams({
                    tab: "applications",
                    applicationId: app.application_code || app.id,
                  });
                }}
              />
            )}
            {activeTab === "applications" && selectedApplication && (
              <ApplicationTrackingTimeline
                applicationId={selectedApplication.application_id || selectedApplication.id}
                applicationCode={selectedApplication.application_code}
                onBack={() => setSelectedApplication(null)}
              />
            )}
            {activeTab === "documents" && <DocumentsTab primaryColor={primaryColor} />}
          </div>
        </div>
      </div>

      <CareerFooter settings={settings} />
    </div>
  );
};

export default CandidateDashboard;